/**
 * SKU Flow Registry — maps SKU code to flow route or status.
 *
 * Kinds:
 *   - live            → render "Begin the rite" enabled, navigate to flowRoute
 *   - stub            → "Available soon" caption (default fallback)
 *   - not_implemented → "Not available yet" caption (explicit, for SKUs that
 *                       are in the catalog but intentionally not wired)
 *
 * Live entries for avatar SKUs additionally carry presentation metadata
 * (variant + extra screens + selfie requirement) consumed by AvatarFlow.
 * See specs/023-avatar-ui-fidelity/data-model.md §2.
 */

import type { AvatarResultGridVariant } from '../types/avatarFlow';

export type AvatarExtraScreen = 'hd-scene-pick' | 'zodiac-progress' | 'cinematic-tabs';

export type SkuFlowEntry =
  | {
      kind: 'live';
      flowRoute: string;
      resultGridVariant?: AvatarResultGridVariant;
      extraScreens?: AvatarExtraScreen[];
      selfieRequired?: boolean;
    }
  | { kind: 'stub' }
  | { kind: 'not_implemented' };

export const SKU_FLOW_REGISTRY: Record<string, SkuFlowEntry> = {
  moon_phase_soulmate: { kind: 'live', flowRoute: '/flow/moon-phase-soulmate' },

  avatar_mini: {
    kind: 'live',
    flowRoute: '/flow/avatar/avatar_mini',
    resultGridVariant: 'grid-3x3',
    selfieRequired: true,
  },
  avatar_max: {
    kind: 'live',
    flowRoute: '/flow/avatar/avatar_max',
    resultGridVariant: 'grid-4x4',
    selfieRequired: true,
  },
  zodiac_collection: {
    kind: 'live',
    flowRoute: '/flow/avatar/zodiac_collection',
    resultGridVariant: 'zodiac-carousel-12',
    extraScreens: ['zodiac-progress'],
    selfieRequired: true,
  },

  compat_check:    { kind: 'live', flowRoute: '/flow/compat' },

  couple_portrait: { kind: 'live', flowRoute: '/flow/couple' },
  future_children: { kind: 'not_implemented' },

  past_lives_pack: {
    kind: 'live',
    flowRoute: '/flow/past-lives/past_lives_pack',
    selfieRequired: true,
  },
};

/**
 * Returns the flow entry for a given SKU code.
 * Defaults to { kind: 'stub' } for any unregistered code.
 */
export function getSkuFlow(code: string): SkuFlowEntry {
  return SKU_FLOW_REGISTRY[code] ?? { kind: 'stub' };
}
